











































































































































































































































import { Component, Vue } from 'vue-property-decorator'
import ApiClient from '@/services/api-client'
import { CvValue, EditorialProject, ProductBulkRow, ProductBulkRowResult, TeamMember } from '@/models'
import {
  CV_VALUE_COUNTRY,
  CV_VALUE_COURSE_LEVEL,
  CV_VALUE_EDUCATION,
  CV_VALUE_LANGUAGE,
  CV_VALUE_PRODUCT_COLLECTION,
  CV_VALUE_PRODUCT_SERIES,
  CV_VALUE_PRODUCT_WORK_TYPE,
  CV_VALUE_REGION,
  CV_VALUE_SUBJECT,
  CV_VALUE_TYPE_PROD
} from '@/utils/constants'
import { CvValueUtils } from '@/utils/cv-value-utils'
import { ActionTypes } from '@/store/action-types'
import { Utils } from '@/utils'
import { ProductFieldRules, loadMaterialTypes } from '@/utils/product-fields'
import { TemplateColumn, buildProductTemplate, downloadBlob, readProductTemplate } from '@/utils/product-bulk-template'

type ColumnKind = 'text' | 'date' | 'cv' | 'cvMulti' | 'year' | 'team' | 'bool' | 'level'
type CellValue = string | string[] | boolean
type RowStatus = 'idle' | 'saving' | 'valid' | 'error' | 'copy_failed'
type FieldErrors = { [field: string]: string }

interface Column {
  field: string;
  kind: ColumnKind;
  labelKey: string;
  // CV type of cv / cvMulti columns
  cvType?: string;
}

interface BulkRowState {
  key: number;
  selected: boolean;
  data: { [field: string]: CellValue };
  status: RowStatus;
  serverErrors: FieldErrors;
  // Fields the user has edited: their errors show before the first submit
  touched: { [field: string]: boolean };
  message: string;
}

const label = (field: string) => `upsert_product_modal_component.${field}_label`

// Every column the sheet (and its Excel template) can have, in display order; the project
// decides which ones show. The WW fields and the Sharepoint URL are only in the product form.
const COLUMNS: Column[] = [
  { field: 'code', kind: 'text', labelKey: 'bulk_product_modal.columns.code' },
  { field: 'name', kind: 'text', labelKey: 'bulk_product_modal.columns.name' },
  { field: 'type', kind: 'cv', labelKey: label('type'), cvType: CV_VALUE_TYPE_PROD },
  { field: 'country', kind: 'cv', labelKey: label('country'), cvType: CV_VALUE_COUNTRY },
  { field: 'organizationalLevel1', kind: 'level', labelKey: label('organizational_level_1') },
  { field: 'organizationalLevel2', kind: 'level', labelKey: label('organizational_level_2') },
  { field: 'campaign', kind: 'year', labelKey: label('campaign') },
  { field: 'productionCampaign', kind: 'year', labelKey: label('production_campaign') },
  { field: 'language', kind: 'cv', labelKey: label('language'), cvType: CV_VALUE_LANGUAGE },
  { field: 'region', kind: 'cvMulti', labelKey: label('region_label'), cvType: CV_VALUE_REGION },
  { field: 'subject', kind: 'cv', labelKey: label('subject_label'), cvType: CV_VALUE_SUBJECT },
  { field: 'educationLevel1', kind: 'cv', labelKey: label('education_level_1'), cvType: CV_VALUE_EDUCATION },
  { field: 'educationLevel2', kind: 'cv', labelKey: label('education_level_2'), cvType: CV_VALUE_COURSE_LEVEL },
  { field: 'developmentManager', kind: 'team', labelKey: label('development_manager') },
  { field: 'workType', kind: 'cv', labelKey: label('work_type'), cvType: CV_VALUE_PRODUCT_WORK_TYPE },
  { field: 'assetsActive', kind: 'bool', labelKey: label('assets_active') },
  { field: 'sendBtovenFromStructureLdx', kind: 'bool', labelKey: label('send_btoven_from_structure_ldx') },
  { field: 'btovenCodeCourse', kind: 'text', labelKey: label('btoven_code_course') },
  { field: 'btovenCodeSubject', kind: 'text', labelKey: label('btoven_code_subject') },
  { field: 'developmentProject', kind: 'text', labelKey: label('development_project') },
  { field: 'layoutManager', kind: 'team', labelKey: label('layout_manager') },
  { field: 'collection', kind: 'cv', labelKey: label('collection'), cvType: CV_VALUE_PRODUCT_COLLECTION },
  { field: 'series', kind: 'cv', labelKey: label('series'), cvType: CV_VALUE_PRODUCT_SERIES },
  { field: 'author', kind: 'text', labelKey: label('author') },
  { field: 'illustrator', kind: 'text', labelKey: label('illustrator') },
  { field: 'readingAge', kind: 'text', labelKey: label('reading_age') },
  { field: 'publicationDate', kind: 'date', labelKey: label('publication_date') },
  { field: 'sourceCode', kind: 'text', labelKey: 'bulk_product_modal.columns.sourceCode' }
]
const LEVEL_FIELDS = ['organizationalLevel1', 'organizationalLevel2']
// Metadata stored as the CV short code ('edct-pri' -> 'pri'), like the product form does
const SHORT_CODE_FIELDS = ['language', 'subject', 'educationLevel1', 'educationLevel2']

// Same rule as the backend (Constants.STRICT_CODSAP_CHECKING)
const CODSAP_REGEX = /^\d{6}[DWM]?$/
const NAME_MAX_LENGTH = 255
// Rows sent per request: the backend accepts up to 50, smaller chunks give a smoother progress
// bar since copying a structure can take a while
const CHUNK_SIZE = 10

const emptyValue = (column: Column): CellValue =>
  column.kind === 'bool' ? false : column.kind === 'cvMulti' ? [] : ''

const hasValue = (value: CellValue | undefined): boolean =>
  Array.isArray(value) ? value.length > 0 : typeof value === 'boolean' ? true : !!(value || '').trim()

const TEMPLATE_WIDTHS: { [kind in ColumnKind]: number } = {
  text: 22, date: 16, cv: 26, cvMulti: 34, year: 12, team: 28, bool: 10, level: 16
}
const TEMPLATE_BLANK_ROWS = 200

const normalize = (value: string) => Utils.normalizeText(value.trim())

let rowKeyCounter = 0

@Component
export default class BulkCreateProductsModal extends Vue {
  public Utils = Utils
  public MAX_DUPLICATE_TIMES = 100

  public readonly campaigns: string[] = Utils.getYearsForCampaign(2019).map(year => year.toString())

  public projectId = ''
  public rows: BulkRowState[] = []
  public cvOptions: { [cvType: string]: CvValue[] } = {}
  public teamMembers: TeamMember[] = []
  public materialTypes: CvValue[] = []
  public showErrors = false
  public processing = false
  public progress = 0
  public progressTotal = 0
  public duplicateTimes = 1
  public templateBusy = false
  private duplicateSource: BulkRowState | null = null

  get editorialProjects(): EditorialProject[] {
    return this.$store.getters.creatableEditorialProjects
  }

  get project(): EditorialProject | undefined {
    return this.editorialProjects.find(item => item.id === this.projectId)
  }

  get fieldRules(): ProductFieldRules {
    return new ProductFieldRules(this.materialTypes, this.project)
  }

  get visibleColumns(): Column[] {
    return this.columnsFor(this.project)
  }

  get editableRows(): BulkRowState[] {
    return this.rows.filter(row => !this.isLocked(row))
  }

  get selectedRows(): BulkRowState[] {
    return this.rows.filter(row => row.selected)
  }

  get allSelected(): boolean {
    return this.rows.length > 0 && this.selectedRows.length === this.rows.length
  }

  get someSelected(): boolean {
    return this.selectedRows.length > 0 && !this.allSelected
  }

  get teamMemberOptions(): { label: string; value: string }[] {
    return this.teamMembers.map(member => {
      const name = `${member.firstName} ${member.lastName}`
      return { label: name, value: name }
    })
  }

  get codeCounts(): { [code: string]: number } {
    const counts: { [code: string]: number } = {}
    this.editableRows.forEach(row => {
      const code = (row.data.code as string).trim()
      if (code) counts[code] = (counts[code] || 0) + 1
    })
    return counts
  }

  get clientErrors(): { [key: number]: FieldErrors } {
    const result: { [key: number]: FieldErrors } = {}
    this.editableRows.forEach(row => { result[row.key] = this.validateRow(row) })
    return result
  }

  get validRows(): BulkRowState[] {
    if (!this.project) return []
    return this.editableRows.filter(row => Object.keys(this.clientErrors[row.key] || {}).length === 0)
  }

  get duplicateTimesValid(): boolean {
    return Number.isInteger(this.duplicateTimes) && this.duplicateTimes >= 1 && this.duplicateTimes <= this.MAX_DUPLICATE_TIMES
  }

  created() {
    this.$store.dispatch(ActionTypes.LOAD_EDITORIAL_PROJECTS)
    this.loadOptions()
  }

  private async loadOptions() {
    const cvTypes = COLUMNS.filter(column => column.cvType).map(column => column.cvType as string)
    const [cvValues, teamMembers, materialTypes] = await Promise.all([
      Promise.all(cvTypes.map(type => CvValueUtils.getCvValuesByType(type))),
      ApiClient.getTeamMembers({ type: '' }),
      loadMaterialTypes()
    ])
    const cvOptions: { [cvType: string]: CvValue[] } = {}
    cvTypes.forEach((type, index) => { cvOptions[type] = cvValues[index] })
    this.cvOptions = cvOptions
    this.teamMembers = teamMembers
    this.materialTypes = materialTypes
  }

  public onShow() {
    // Products already created (with a failed structure copy) were reported in the last run
    this.rows = this.rows.filter(row => row.status !== 'copy_failed')
    this.showErrors = false
    if (!this.rows.length) this.addRow()
  }

  public close() {
    this.$bvModal.hide('BulkCreateProductsModal')
  }

  // ----- Columns -----

  private columnsFor(project: EditorialProject | undefined): Column[] {
    if (!project) return []
    const rules = new ProductFieldRules(this.materialTypes, project)
    return COLUMNS.filter(column => {
      if (column.field === 'sourceCode') return true
      if (column.kind === 'level') return this.levelOptionsOf(project, column.field).length > 0
      return rules.isVisible(column.field)
    })
  }

  private levelOptionsOf(project: EditorialProject | undefined, field: string): string[] {
    const values = project ? project[field as 'organizationalLevel1' | 'organizationalLevel2'] : null
    return values ? values.split(',').map(value => value.trim()).filter(value => value) : []
  }

  public isRequired(field: string): boolean {
    if (LEVEL_FIELDS.includes(field)) return this.levelOptionsOf(this.project, field).length > 0
    return field !== 'sourceCode' && this.fieldRules.isRequired(field)
  }

  public columnOptions(column: Column): unknown[] {
    switch (column.kind) {
      case 'cv':
      case 'cvMulti':
        return this.cvOptions[column.cvType as string] || []
      case 'team':
        return this.teamMemberOptions
      case 'level':
        return this.levelOptionsOf(this.project, column.field).map(value => ({ label: value, value }))
      default:
        return this.campaigns.map(value => ({ label: value, value }))
    }
  }

  public optionReducer(column: Column) {
    return column.kind === 'cv' || column.kind === 'cvMulti'
      ? (item: CvValue) => item.codeId
      : (item: { value: string }) => item.value
  }

  public selectValue(row: BulkRowState, column: Column) {
    const value = row.data[column.field]
    return column.kind === 'cvMulti' ? value : (value || null)
  }

  /**
   * Changing the project changes the columns: the values of the columns that go away (and the
   * organizational levels the new project doesn't offer) are cleared after confirming
   */
  public async changeProject(projectId: string | null) {
    if (!projectId || projectId === this.projectId) return
    const newProject = this.editorialProjects.find(item => item.id === projectId)
    const kept = this.columnsFor(newProject).map(column => column.field)
    const cleared = COLUMNS.filter(column => {
      if (column.kind === 'level') {
        return !this.levelOptionsOf(newProject, column.field).length || kept.indexOf(column.field) < 0
      }
      return kept.indexOf(column.field) < 0
    })
    const affected = this.editableRows.filter(row => cleared.some(column => {
      const value = row.data[column.field]
      if (column.kind === 'level' && this.levelOptionsOf(newProject, column.field).includes(value as string)) return false
      return column.kind !== 'bool' && hasValue(value)
    }))

    if (affected.length) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t('bulk_product_modal.change_project_confirm', { count: affected.length }) as string, {
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'warning',
          okTitle: this.$t('button_actions.confirm') as string,
          cancelVariant: 'light',
          cancelTitle: this.$t('button_actions.cancel') as string,
          footerClass: 'p-2',
          centered: true
        })
      if (!confirmed) return
    }

    this.projectId = projectId
    this.editableRows.forEach(row => {
      cleared.forEach(column => {
        const value = row.data[column.field]
        const levelStillValid = column.kind === 'level' && this.levelOptionsOf(newProject, column.field).includes(value as string)
        if (!levelStillValid) row.data[column.field] = emptyValue(column)
      })
      row.serverErrors = {}
      if (row.status === 'valid' || row.status === 'error') {
        row.status = 'idle'
        row.message = ''
      }
    })
  }

  // ----- Rows -----

  private newRow(data?: BulkRowState['data']): BulkRowState {
    const rowData: BulkRowState['data'] = {}
    COLUMNS.forEach(column => {
      const value = data ? data[column.field] : emptyValue(column)
      rowData[column.field] = Array.isArray(value) ? [...value] : value
    })
    return {
      key: ++rowKeyCounter,
      selected: false,
      data: rowData,
      status: 'idle',
      serverErrors: {},
      touched: {},
      message: ''
    }
  }

  public addRow() {
    this.rows.push(this.newRow())
  }

  public duplicateRow(source: BulkRowState, times: number) {
    const index = this.rows.indexOf(source)
    const copies = Array.from({ length: times }, () => this.newRow(source.data))
    this.rows.splice(index + 1, 0, ...copies)
  }

  public openDuplicateTimes(row: BulkRowState) {
    this.duplicateSource = row
    this.duplicateTimes = 1
    this.$bvModal.show('BulkDuplicateRowTimesModal')
  }

  public confirmDuplicateTimes() {
    if (this.duplicateSource) this.duplicateRow(this.duplicateSource, this.duplicateTimes)
    this.duplicateSource = null
    this.$bvModal.hide('BulkDuplicateRowTimesModal')
  }

  public duplicateSelected() {
    const result: BulkRowState[] = []
    this.rows.forEach(row => {
      result.push(row)
      if (row.selected) result.push(this.newRow(row.data))
    })
    this.rows = result
  }

  public removeRow(row: BulkRowState) {
    this.rows = this.rows.filter(item => item !== row)
  }

  public async removeSelected() {
    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('bulk_product_modal.remove_selected_confirm', { count: this.selectedRows.length }) as string, {
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove') as string,
        cancelVariant: 'light',
        cancelTitle: this.$t('button_actions.cancel') as string,
        footerClass: 'p-2',
        centered: true
      })
    if (confirmed) this.rows = this.rows.filter(row => !row.selected)
  }

  public toggleAll(checked: boolean) {
    this.rows.forEach(row => { row.selected = checked })
  }

  public isLocked(row: BulkRowState): boolean {
    return row.status === 'copy_failed'
  }

  public rowClass(row: BulkRowState) {
    return {
      'bulk-row--error': row.status === 'error' || (row.status === 'idle' && !!this.statusMessage(row)),
      'bulk-row--warning': row.status === 'copy_failed',
      'bulk-row--selected': row.selected
    }
  }

  // ----- Editing -----

  public setField(row: BulkRowState, field: string, value: CellValue | null) {
    const column = COLUMNS.find(item => item.field === field) as Column
    row.data[field] = value === null || value === undefined ? emptyValue(column) : value
    this.$set(row.touched, field, true)
    if (row.serverErrors[field]) {
      const serverErrors = { ...row.serverErrors }
      delete serverErrors[field]
      row.serverErrors = serverErrors
    }
    if (row.status === 'valid' || (row.status === 'error' && !Object.keys(row.serverErrors).length)) {
      row.status = 'idle'
      row.message = ''
    }
  }

  /**
   * Copies the value of the first selected row (or the first row) to the other selected rows
   * (or every row)
   */
  public fillColumn(field: string) {
    const editable = this.editableRows
    const selected = editable.filter(row => row.selected)
    const source = selected.length ? selected[0] : editable[0]
    if (!source) return
    const targets = (selected.length > 1 ? selected : editable).filter(row => row !== source)
    const value = source.data[field]
    targets.forEach(row => this.setField(row, field, Array.isArray(value) ? [...value] : value))
  }

  /**
   * Pasting several lines (e.g. a column copied from Excel) fills this row and the following
   * ones, adding rows when needed
   */
  public onPaste(event: ClipboardEvent, rowIndex: number, field: string) {
    const text = event.clipboardData ? event.clipboardData.getData('text') : ''
    const lines = text.replace(/\r/g, '').split('\n')
    if (lines.length && lines[lines.length - 1] === '') lines.pop()
    if (lines.length <= 1) return

    event.preventDefault()
    let index = rowIndex
    lines.forEach(line => {
      while (this.rows[index] && this.isLocked(this.rows[index])) index++
      if (!this.rows[index]) this.rows.push(this.newRow())
      this.setField(this.rows[index], field, line.split('\t')[0].trim())
      index++
    })
  }

  // ----- Excel template -----

  /** Text of each option of a selector column, as shown in the template dropdown */
  private optionLabels(column: Column): string[] {
    if (column.kind === 'bool') return [this.$t('bulk_product_modal.template_yes') as string, this.$t('bulk_product_modal.template_no') as string]
    return (this.columnOptions(column) as { name?: string; label?: string }[])
      .map(option => (column.kind === 'cv' || column.kind === 'cvMulti' ? option.name : option.label) || '')
      .filter(label => label)
  }

  public async downloadTemplate() {
    if (!this.project) return
    this.templateBusy = true
    try {
      const columns: TemplateColumn[] = this.visibleColumns.map(column => ({
        key: column.field,
        header: this.$t(column.labelKey) + (this.isRequired(column.field) ? ' *' : ''),
        width: TEMPLATE_WIDTHS[column.kind],
        options: column.kind === 'text' || column.kind === 'date' ? undefined : this.optionLabels(column),
        text: column.kind === 'text',
        date: column.kind === 'date',
        multiple: column.kind === 'cvMulti'
      }))
      const blob = await buildProductTemplate(this.project.id, columns, {
        sheet: this.$t('bulk_product_modal.template_sheet') as string,
        lists: this.$t('bulk_product_modal.template_lists_sheet') as string
      }, TEMPLATE_BLANK_ROWS)
      downloadBlob(blob, `plantilla-productos-${this.project.id}.xlsx`)
    } catch (e) {
      console.error('Unable to build the bulk product template', e)
      this.toast('danger', this.$t('bulk_product_modal.template_error') as string)
    } finally {
      this.templateBusy = false
    }
  }

  /**
   * Turns a template cell into a sheet value. Selector values are matched by their text (or
   * code), ignoring case and accents, against the options loaded now. Returns undefined when a
   * value can't be resolved, so it is left empty and reported.
   */
  private cellToValue(column: Column, text: string): CellValue | undefined {
    if (!text) return emptyValue(column)
    if (column.kind === 'text' || column.kind === 'date') return text
    if (column.kind === 'bool') {
      const value = normalize(text)
      if (['si', 'yes', 'sim', 'true', '1', 'x', normalize(this.$t('bulk_product_modal.template_yes') as string)].includes(value)) return true
      if (['no', 'nao', 'false', '0', normalize(this.$t('bulk_product_modal.template_no') as string)].includes(value)) return false
      return undefined
    }
    if (column.kind === 'cv' || column.kind === 'cvMulti') {
      const options = this.cvOptions[column.cvType as string] || []
      const find = (part: string) => options.find(option =>
        normalize(option.name || '') === normalize(part) || option.codeId === part.trim())
      if (column.kind === 'cvMulti') {
        const found = text.split(',').filter(part => part.trim()).map(find)
        return found.every(option => option) ? found.map(option => (option as CvValue).codeId) : undefined
      }
      const option = find(text)
      return option ? option.codeId : undefined
    }
    const option = (this.columnOptions(column) as { label: string; value: string }[])
      .find(item => normalize(item.label) === normalize(text))
    return option ? option.value : undefined
  }

  public async onTemplateSelected(event: Event) {
    const input = event.target as HTMLInputElement
    const file = input.files && input.files[0]
    if (!file) return
    this.templateBusy = true
    try {
      const content = await readProductTemplate(file)
      const project = this.editorialProjects.find(item => item.id === content.projectId)
      if (!project) {
        this.toast('danger', this.$t('bulk_product_modal.template_other_project') as string)
        return
      }
      if (!content.rows.length) {
        this.toast('warning', this.$t('bulk_product_modal.template_empty') as string)
        return
      }
      const filledRows = this.editableRows.filter(row => COLUMNS.some(column => column.kind !== 'bool' && hasValue(row.data[column.field])))
      if (filledRows.length) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          this.$t('bulk_product_modal.template_replace_confirm', { count: filledRows.length }) as string, {
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'warning',
            okTitle: this.$t('button_actions.confirm') as string,
            cancelVariant: 'light',
            cancelTitle: this.$t('button_actions.cancel') as string,
            footerClass: 'p-2',
            centered: true
          })
        if (!confirmed) return
      }

      // The template brings its project; its columns are the ones of that project
      this.projectId = project.id
      let unresolved = 0
      const imported = content.rows.map(values => {
        const row = this.newRow()
        this.visibleColumns.forEach(column => {
          const value = this.cellToValue(column, values[column.field] || '')
          if (value === undefined) unresolved++
          else row.data[column.field] = value
        })
        return row
      })
      // Same as the asset import: the file replaces the sheet, mixing both is a recipe for not
      // knowing what is being saved
      this.rows = imported
      this.showErrors = true
      this.toast('success', this.$t('bulk_product_modal.template_imported', { count: imported.length }) as string)
      if (unresolved) this.toast('warning', this.$t('bulk_product_modal.template_unresolved', { count: unresolved }) as string)
    } catch (e) {
      console.error('Unable to read the bulk product template', e)
      this.toast('danger', this.$t('bulk_product_modal.template_error') as string)
    } finally {
      this.templateBusy = false
      input.value = ''
    }
  }

  private toast(variant: string, message: string) {
    this.$store.dispatch(ActionTypes.SHOW_TOAST, { title: this.$t('bulk_product_modal.title'), variant, message })
  }

  // ----- Validation -----

  private validateRow(row: BulkRowState): FieldErrors {
    const errors: FieldErrors = {}
    const data = row.data
    const code = (data.code as string).trim()
    const required = this.$t('bulk_product_modal.errors.required') as string

    this.visibleColumns.forEach(column => {
      if (column.kind !== 'bool' && this.isRequired(column.field) && !hasValue(data[column.field])) {
        errors[column.field] = required
      }
    })

    if (code && !CODSAP_REGEX.test(code)) errors.code = this.$t('bulk_product_modal.errors.code_format') as string
    else if (code && this.codeCounts[code] > 1) errors.code = this.$t('bulk_product_modal.errors.code_repeated') as string

    const name = (data.name as string).trim()
    if (name.length > NAME_MAX_LENGTH) errors.name = this.$t('bulk_product_modal.errors.name_length', { max: NAME_MAX_LENGTH }) as string

    const sourceCode = (data.sourceCode as string).trim()
    if (sourceCode && !CODSAP_REGEX.test(sourceCode)) errors.sourceCode = this.$t('bulk_product_modal.errors.code_format') as string
    else if (sourceCode && sourceCode === code) errors.sourceCode = this.$t('bulk_product_modal.errors.source_itself') as string

    return errors
  }

  /**
   * A field error shows as soon as the field has a value or was edited, and for every field
   * after the first submit (so empty required cells are not red from the start)
   */
  private isErrorVisible(row: BulkRowState, field: string): boolean {
    const value = row.data[field]
    return this.showErrors || !!row.touched[field] || (typeof value !== 'boolean' && hasValue(value))
  }

  private visibleClientErrors(row: BulkRowState): string[] {
    const errors = this.clientErrors[row.key] || {}
    return Object.keys(errors).filter(field => this.isErrorVisible(row, field))
  }

  public fieldError(row: BulkRowState, field: string): string | null {
    const clientError = (this.clientErrors[row.key] || {})[field]
    if (clientError && this.isErrorVisible(row, field)) return clientError
    return row.serverErrors[field] || null
  }

  public statusMessage(row: BulkRowState): string {
    if (row.status === 'saving') return ''
    if (row.status === 'valid') return this.$t('bulk_product_modal.status.valid') as string
    if (row.status === 'copy_failed') return this.$t('bulk_product_modal.status.copy_failed', { message: row.message }) as string
    if (row.message) return row.message
    // Server errors on fields without a column (e.g. the production project) are shown here
    const columnFields = this.visibleColumns.map(column => column.field)
    const otherErrors = Object.keys(row.serverErrors).filter(field => !columnFields.includes(field))
    if (otherErrors.length) return otherErrors.map(field => row.serverErrors[field]).join('. ')
    if (Object.keys(row.serverErrors).length) return this.$t('bulk_product_modal.status.check_fields') as string
    if (this.visibleClientErrors(row).length) return this.$t('bulk_product_modal.status.check_fields') as string
    return ''
  }

  // ----- Submit -----

  private toPayload(row: BulkRowState): ProductBulkRow {
    const visible = this.visibleColumns.map(column => column.field)
    const text = (field: string) => visible.includes(field) ? ((row.data[field] as string) || '').trim() || null : null
    const metadata: { [key: string]: string | boolean } = {}
    this.visibleColumns.forEach(column => {
      const value = row.data[column.field]
      if (['code', 'name', 'type', 'country', 'workType', 'sourceCode', 'campaign', 'productionCampaign'].includes(column.field) ||
        LEVEL_FIELDS.includes(column.field)) return
      if (column.kind === 'bool') {
        metadata[column.field] = value as boolean
      } else if (column.kind === 'cvMulti') {
        if ((value as string[]).length) metadata[column.field] = (value as string[]).join(',')
      } else if (hasValue(value)) {
        const stringValue = (value as string).trim()
        metadata[column.field] = SHORT_CODE_FIELDS.includes(column.field) && column.cvType &&
          stringValue.startsWith(column.cvType + '-')
          ? stringValue.substring(column.cvType.length + 1)
          : stringValue
        if (column.field === 'subject') {
          const subject = (this.cvOptions[column.cvType as string] || []).find(item => item.codeId === stringValue)
          if (subject && subject.name) metadata.subjectLabel = subject.name
        }
      }
    })
    return {
      code: (row.data.code as string).trim(),
      name: (row.data.name as string).trim(),
      type: row.data.type as string,
      editorialProject: this.projectId,
      country: text('country'),
      organizationalLevel1: text('organizationalLevel1'),
      organizationalLevel2: text('organizationalLevel2'),
      campaign: text('campaign'),
      productionCampaign: text('productionCampaign'),
      sourceCode: text('sourceCode'),
      workType: text('workType'),
      metadata
    }
  }

  private applyResult(row: BulkRowState, result: ProductBulkRowResult | undefined) {
    const serverErrors: FieldErrors = {}
    if (result) result.errors.forEach(error => { serverErrors[error.field] = error.message })
    row.serverErrors = serverErrors
    row.message = (result && result.message) || ''
    switch (result ? result.status : 'ERROR') {
      case 'VALID': row.status = 'valid'; break
      case 'CREATED': row.status = 'idle'; break
      case 'CREATED_COPY_FAILED': row.status = 'copy_failed'; break
      default: row.status = 'error'
    }
  }

  public async submit(dryRun: boolean) {
    this.showErrors = true
    const candidates = this.validRows
    // Rows with errors in the sheet are not sent, but they count as failed in the summary
    const skipped = this.editableRows.length - candidates.length
    if (!candidates.length) {
      this.$store.dispatch(ActionTypes.SHOW_TOAST, {
        title: this.$t('bulk_product_modal.title'),
        variant: 'warning',
        message: this.$t('bulk_product_modal.feedback.no_valid_rows')
      })
      return
    }

    this.processing = true
    this.progress = 0
    this.progressTotal = candidates.length
    const created: BulkRowState[] = []
    let copyFailed = 0
    let failed = 0
    let aborted = false
    try {
      for (let start = 0; start < candidates.length; start += CHUNK_SIZE) {
        const chunk = candidates.slice(start, start + CHUNK_SIZE)
        chunk.forEach(row => { row.status = 'saving' })
        let results: ProductBulkRowResult[]
        try {
          results = await ApiClient.createProductsBulk(chunk.map(row => this.toPayload(row)), dryRun)
        } catch (e) {
          chunk.forEach(row => { row.status = 'idle' })
          throw e
        }
        chunk.forEach((row, index) => {
          const result = results.find(item => item.index === index)
          this.applyResult(row, result)
          const status = result ? result.status : 'ERROR'
          if (status === 'CREATED') created.push(row)
          else if (status === 'CREATED_COPY_FAILED') copyFailed++
          else if (status !== 'VALID') failed++
        })
        this.progress += chunk.length
      }
    } catch (e) {
      aborted = true
      const message = (e.response && e.response.data && e.response.data.message) || e.message
      this.$store.dispatch(ActionTypes.SHOW_TOAST, {
        title: this.$t('feedback.error_title_generic'),
        variant: 'danger',
        message: this.$t('feedback.error_message_generic') + '. ' + message
      })
    } finally {
      this.processing = false
    }

    failed += skipped
    if (dryRun) {
      if (aborted) return
      this.$store.dispatch(ActionTypes.SHOW_TOAST, {
        title: this.$t('bulk_product_modal.title'),
        variant: failed ? 'warning' : 'success',
        message: failed
          ? this.$t('bulk_product_modal.feedback.validated_with_errors', { valid: candidates.length - (failed - skipped), invalid: failed })
          : this.$t('bulk_product_modal.feedback.validated')
      })
      return
    }

    // Created rows leave the sheet, failed ones stay to be fixed and sent again
    this.rows = this.rows.filter(row => !created.includes(row))
    if (created.length || copyFailed) this.$emit('productCreated')
    if (aborted || (!created.length && !copyFailed && !failed)) return

    const total = created.length + copyFailed
    if (!failed && !copyFailed) {
      this.$store.dispatch(ActionTypes.SHOW_TOAST, {
        title: this.$t('bulk_product_modal.title'),
        variant: 'success',
        message: this.$t('bulk_product_modal.feedback.created', { count: total })
      })
      if (!this.rows.length) this.close()
    } else {
      this.$store.dispatch(ActionTypes.SHOW_TOAST, {
        title: this.$t('bulk_product_modal.title'),
        variant: total ? 'warning' : 'danger',
        message: this.$t('bulk_product_modal.feedback.partial', { created: total, copyFailed, failed })
      })
    }
  }
}
